import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Screen } from '@tempmee/react';
import { BackButton, Column, Text } from '~/commonComponents';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { clearAllReceipts, fetchJobsWithTransactions, fetchReceiptList } from '../../../actions';
import { paymentHistoryFilterOptions } from '../../../constants';
import { PostingPaymentHistoryTable, RequestReportPopup, Toolbar } from './components';
import { ReceiptTableV2 } from './components/AllReceiptTable/ReceiptTableV2';
import { getFilterDateRage } from './utils';
import '../../../themes/global.scss';

const LIMIT_VALUE = 25;

export default function ReceiptList() {
  const dispatch = useDispatch();
  const receiptsScrollRef = useRef(null);

  const isFinalPage = useSelector((state) => state.job.isFinalPage);
  const isActiveRequest = useSelector((state) => state.job.isActiveRequest);

  const jobsWithTransactionPagination = useSelector(
    (state) => state.job.jobsWithTransactionPagination,
  );
  const receiptsPagination = useSelector((state) => state.job.receiptsPagination);
  const receiptDisplayMode = useSelector((state) => state.job.receiptDisplayMode);
  const [sort, setSort] = useState({
    field: 'date',
    order: 'desc',
  });
  const [filter, setFilter] = useState(paymentHistoryFilterOptions.ALL);
  const [isShowRequestReportPopup, setIsShowRequestReportPopup] = useState(false);

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  useEffect(() => {
    // Reset filter when changing display mode
    setFilter(paymentHistoryFilterOptions.ALL);
  }, [receiptDisplayMode]);

  useEffect(() => {
    dispatch(clearAllReceipts());

    if (receiptDisplayMode === 'shift') {
      dispatch(
        fetchJobsWithTransactions({
          page: 1,
          limit: LIMIT_VALUE,
          sort: sort.field,
          order: sort.order,
        }),
      );
    }
  }, [dispatch, receiptDisplayMode, sort, filter]);

  const handleScroll = () => {
    const receiptsScroll = receiptsScrollRef.current;

    const userScrollHeight = receiptsScroll.scrollHeight - receiptsScroll?.scrollTop - 20;
    const clientHeight = receiptsScroll?.clientHeight;

    if (receiptDisplayMode === 'all') {
      if (userScrollHeight <= clientHeight && !isActiveRequest && !isFinalPage) {
        dispatch(
          fetchReceiptList({
            page: receiptsPagination?.page + 1,
            limit: LIMIT_VALUE,
            sort: sort.field,
            order: sort.order,
            ...getFilterDateRage(filter),
          }),
        );
      }
    } else if (
      userScrollHeight <= clientHeight &&
      !isActiveRequest &&
      !jobsWithTransactionPagination?.isFinal
    ) {
      dispatch(
        fetchJobsWithTransactions({
          page: jobsWithTransactionPagination?.page + 1,
          limit: LIMIT_VALUE,
        }),
      );
    }
  };

  const changeSort = (sortName) => {
    setSort({
      field: sortName,
      order: sort.order === 'asc' ? 'desc' : 'asc',
    });

    if (receiptsScrollRef.current) {
      receiptsScrollRef.current.scrollTop = 0;
    }
  };

  return (
    <>
      <Screen variant="standard" data-cy="payment-history-container">
        <Screen.Header start={<BackButton />} title="Payment History" />
        <Screen.Body>
          <Column alignItems="center" justifyContent="center">
            <Text fontSize={22}>
              {receiptDisplayMode === 'shift'
                ? `By ${isEnforcePermanentJobV2 ? 'Posting' : 'Shift'}`
                : 'All Transactions'}
            </Text>
          </Column>

          <Toolbar
            displayMode={receiptDisplayMode}
            filter={filter}
            onFilterChange={(val) => setFilter(val)}
            onShowRequestReportPopup={() => setIsShowRequestReportPopup(true)}
          />

          {receiptDisplayMode === 'all' ? (
            <ReceiptTableV2
              filters={{
                transactionDate: filter,
              }}
            />
          ) : (
            <PostingPaymentHistoryTable
              ref={receiptsScrollRef}
              sort={{
                direction: sort.order === 'asc' ? 'ASC' : 'DESC',
                field: sort.field,
              }}
              onScroll={handleScroll}
              onSort={changeSort}
            />
          )}
        </Screen.Body>
      </Screen>
      {isShowRequestReportPopup && (
        <RequestReportPopup onClose={() => setIsShowRequestReportPopup(false)} />
      )}
    </>
  );
}
